import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Undo2, Trash2, Plus, ArrowDownCircle, Gift, Loader2, ListChecks } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { API, PRODUCT_LABELS, fmtChf } from "./utils";

const TYPE_META = {
  offline_sale: {
    label: "Verkauf offline",
    icon: ArrowDownCircle,
    color: "text-[#00A651]",
    borderColor: "border-[#00A651]/40",
    bgColor: "bg-[#00A651]/5",
  },
  giveaway: {
    label: "Marketing Giveaway",
    icon: Gift,
    color: "text-yellow-400",
    borderColor: "border-yellow-500/40",
    bgColor: "bg-yellow-500/5",
  },
};

const emptyForm = {
  type: "offline_sale",
  slug: "mamba-edition",
  size: "9",
  quantity: 1,
  unit_price_chf: 99,
  discount_pct: 0,
  note: "",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-CH", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const StockMovementsPanel = ({ inventory, onChanged }) => {
  const { token } = useAuth();
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const skuOptions = useMemo(() => {
    // Group sizes per slug from actual inventory rows
    const map = {};
    (inventory || []).forEach((i) => {
      map[i.slug] = map[i.slug] || [];
      map[i.slug].push(i.size);
    });
    return map;
  }, [inventory]);

  const fetchMovements = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/stock-movements`, { headers: authHeaders });
      setMovements(r.data);
    } catch (err) {
      toast.error("Bewegungen konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMovements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const availableSizes = skuOptions[form.slug] || [];

  // Keep size selection valid whenever slug changes
  useEffect(() => {
    if (!availableSizes.length) return;
    const cur = form.size;
    const normalized = cur === "" ? null : cur;
    if (!availableSizes.some((s) => (s ?? null) === normalized)) {
      setForm((f) => ({ ...f, size: availableSizes[0] ?? "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.slug]);

  const resetForm = () => setForm(emptyForm);

  const submitMovement = async (e) => {
    e.preventDefault();
    const qty = Number(form.quantity);
    if (!qty || qty < 1) {
      toast.error("Menge muss mindestens 1 sein");
      return;
    }
    const payload = {
      type: form.type,
      slug: form.slug,
      size: form.size === "" || form.size === null ? null : String(form.size),
      quantity: qty,
      note: form.note || null,
    };
    if (form.type === "offline_sale") {
      const price = Number(form.unit_price_chf);
      if (!price || price <= 0) {
        toast.error("Bitte Stückpreis eingeben");
        return;
      }
      payload.unit_price_chf = price;
      payload.discount_pct = Number(form.discount_pct) || 0;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/stock-movements`, payload, { headers: authHeaders });
      toast.success("Bewegung erfasst");
      resetForm();
      setShowForm(false);
      await fetchMovements();
      onChanged?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Fehler beim Speichern";
      toast.error(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const reverseMovement = async (m) => {
    if (!window.confirm(
      `Bewegung rückgängig machen? \n\n${TYPE_META[m.type]?.label} · ${m.quantity}x ${PRODUCT_LABELS[m.slug] || m.slug}${m.size ? ` (Gr. ${m.size})` : ""}\n\nDer Bestand wird um ${m.quantity} erhöht.`
    )) return;
    try {
      await axios.post(`${API}/admin/stock-movements/${m.id}/reverse`, {}, { headers: authHeaders });
      toast.success("Rückgebucht");
      await fetchMovements();
      onChanged?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Fehler beim Rückbuchen";
      toast.error(String(msg));
    }
  };

  const deleteMovement = async (m) => {
    if (!window.confirm("Diese rückgebuchte Bewegung endgültig aus der Historie löschen?")) return;
    try {
      await axios.delete(`${API}/admin/stock-movements/${m.id}`, { headers: authHeaders });
      toast.success("Gelöscht");
      await fetchMovements();
    } catch (err) {
      toast.error("Fehler beim Löschen");
    }
  };

  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-6" data-testid="stock-movements-card">
      <div className="flex items-center gap-3 mb-5">
        <ListChecks className="w-5 h-5 text-[#00A651]" />
        <h2 className="font-condensed uppercase tracking-widest text-sm text-white">
          Manuelle Bewegungen
        </h2>
        <span className="text-[10px] font-mono text-white/40">
          offline Verkäufe · Marketing
        </span>
        <button
          onClick={() => setShowForm((v) => !v)}
          data-testid="new-movement-btn"
          className="ml-auto inline-flex items-center gap-1.5 border border-[#00A651]/40 hover:bg-[#00A651] hover:text-black text-[#00A651] font-condensed uppercase tracking-widest text-[10px] px-3 py-1.5 transition-colors"
        >
          <Plus className="w-3 h-3" />
          {showForm ? "Abbrechen" : "Neue Bewegung"}
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={submitMovement}
          className="border border-white/10 bg-black p-4 mb-5 space-y-3"
          data-testid="new-movement-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block font-condensed uppercase tracking-widest text-[10px] text-white/50 mb-1">
                Typ
              </label>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                data-testid="movement-type"
                className="w-full bg-black border border-white/20 text-white text-sm px-3 py-2 focus:border-[#00A651] outline-none"
              >
                <option value="offline_sale">Verkauf offline (mit Preis)</option>
                <option value="giveaway">Marketing Giveaway (kostenlos)</option>
              </select>
            </div>
            <div>
              <label className="block font-condensed uppercase tracking-widest text-[10px] text-white/50 mb-1">
                Produkt
              </label>
              <select
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                data-testid="movement-slug"
                className="w-full bg-black border border-white/20 text-white text-sm px-3 py-2 focus:border-[#00A651] outline-none"
              >
                {Object.keys(skuOptions).map((slug) => (
                  <option key={slug} value={slug}>
                    {PRODUCT_LABELS[slug] || slug}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block font-condensed uppercase tracking-widest text-[10px] text-white/50 mb-1">
                Größe
              </label>
              <select
                value={form.size ?? ""}
                onChange={(e) => setForm({ ...form, size: e.target.value })}
                data-testid="movement-size"
                className="w-full bg-black border border-white/20 text-white text-sm px-3 py-2 focus:border-[#00A651] outline-none"
              >
                {availableSizes.map((s, idx) => (
                  <option key={`${s ?? "no-size"}-${idx}`} value={s ?? ""}>
                    {s ? `Größe ${s}` : "One Size"}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block font-condensed uppercase tracking-widest text-[10px] text-white/50 mb-1">
                Menge
              </label>
              <input
                type="number"
                min={1}
                max={999}
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                data-testid="movement-quantity"
                className="w-full bg-black border border-white/20 text-white text-sm px-3 py-2 focus:border-[#00A651] outline-none"
              />
            </div>
            {form.type === "offline_sale" && (
              <>
                <div>
                  <label className="block font-condensed uppercase tracking-widest text-[10px] text-white/50 mb-1">
                    Stückpreis (CHF)
                  </label>
                  <input
                    type="number"
                    min={0}
                    step="0.5"
                    value={form.unit_price_chf}
                    onChange={(e) => setForm({ ...form, unit_price_chf: e.target.value })}
                    data-testid="movement-price"
                    className="w-full bg-black border border-white/20 text-white text-sm px-3 py-2 focus:border-[#00A651] outline-none"
                  />
                </div>
                <div>
                  <label className="block font-condensed uppercase tracking-widest text-[10px] text-white/50 mb-1">
                    Rabatt (%)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step="1"
                    value={form.discount_pct}
                    onChange={(e) => setForm({ ...form, discount_pct: e.target.value })}
                    data-testid="movement-discount"
                    className="w-full bg-black border border-white/20 text-white text-sm px-3 py-2 focus:border-[#00A651] outline-none"
                  />
                </div>
              </>
            )}
          </div>
          <div>
            <label className="block font-condensed uppercase tracking-widest text-[10px] text-white/50 mb-1">
              Notiz (optional)
            </label>
            <input
              type="text"
              maxLength={300}
              placeholder='z.B. "Turnier Zürich" oder "Sponsoring FC Wädenswil"'
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              data-testid="movement-note"
              className="w-full bg-black border border-white/20 text-white text-sm px-3 py-2 focus:border-[#00A651] outline-none"
            />
          </div>
          {form.type === "offline_sale" && (
            <div className="text-[11px] text-white/50 font-mono">
              Umsatz-Vorschau:{" "}
              <span className="text-[#00A651]">
                {fmtChf(
                  Math.max(
                    0,
                    (Number(form.unit_price_chf) || 0) *
                      (Number(form.quantity) || 0) *
                      (1 - (Number(form.discount_pct) || 0) / 100)
                  )
                )}
              </span>
            </div>
          )}
          <div className="flex items-center gap-3 pt-1">
            <button
              type="submit"
              disabled={saving}
              data-testid="movement-submit"
              className="inline-flex items-center gap-2 bg-[#00A651] hover:bg-[#008A43] text-black disabled:opacity-50 font-condensed uppercase tracking-widest text-xs px-4 py-2 transition-colors"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
              Speichern & Bestand aktualisieren
            </button>
            <button
              type="button"
              onClick={() => {
                resetForm();
                setShowForm(false);
              }}
              className="text-white/60 hover:text-white font-condensed uppercase tracking-widest text-xs"
            >
              Abbrechen
            </button>
          </div>
        </form>
      )}

      {/* History */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="text-white/50 text-sm py-6 text-center">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
            Lade Bewegungen...
          </div>
        ) : movements.length === 0 ? (
          <div className="text-white/50 text-sm py-6 text-center border border-dashed border-white/10">
            Noch keine manuellen Bewegungen erfasst.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="movements-table">
            <thead>
              <tr className="text-left font-condensed uppercase tracking-widest text-[10px] text-white/50 border-b border-white/10">
                <th className="py-2 pr-3">Datum</th>
                <th className="py-2 pr-3">Typ</th>
                <th className="py-2 pr-3">Produkt</th>
                <th className="py-2 pr-3 text-right">Menge</th>
                <th className="py-2 pr-3 text-right">Umsatz</th>
                <th className="py-2 pr-3">Notiz</th>
                <th className="py-2 pr-3 text-right">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {movements.map((m) => {
                const meta = TYPE_META[m.type] || {};
                const Icon = meta.icon;
                return (
                  <tr
                    key={m.id}
                    data-testid={`movement-row-${m.id}`}
                    className={`border-b border-white/5 ${m.reversed ? "opacity-40" : ""}`}
                  >
                    <td className="py-2 pr-3 text-white/70 font-mono text-xs">
                      {fmtDate(m.created_at)}
                    </td>
                    <td className={`py-2 pr-3 ${meta.color}`}>
                      <span className="inline-flex items-center gap-1.5">
                        {Icon && <Icon className="w-3.5 h-3.5" />}
                        <span className="text-xs">{meta.label || m.type}</span>
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-white">
                      {PRODUCT_LABELS[m.slug] || m.slug}
                      {m.size ? (
                        <span className="text-white/50 text-xs ml-1">Gr. {m.size}</span>
                      ) : (
                        <span className="text-white/50 text-xs ml-1">One Size</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-right text-white font-mono">
                      {m.reversed ? (
                        <span className="line-through">{m.quantity}</span>
                      ) : (
                        m.quantity
                      )}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono">
                      {m.type === "offline_sale" ? (
                        <span className="text-[#00A651]">
                          {fmtChf(m.revenue_chf)}
                          {m.discount_pct ? (
                            <span className="text-white/50 text-[10px] ml-1">
                              (−{m.discount_pct}%)
                            </span>
                          ) : null}
                        </span>
                      ) : (
                        <span className="text-white/40 text-xs">—</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-white/60 text-xs max-w-[220px] truncate">
                      {m.note || "—"}
                      {m.reversed && (
                        <span className="ml-1 text-yellow-400 text-[10px] font-condensed uppercase tracking-widest">
                          · rückgebucht
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-right">
                      {m.reversed ? (
                        <button
                          onClick={() => deleteMovement(m)}
                          data-testid={`movement-delete-${m.id}`}
                          className="inline-flex items-center gap-1 text-white/40 hover:text-red-400 font-condensed uppercase tracking-widest text-[10px]"
                        >
                          <Trash2 className="w-3 h-3" />
                          Löschen
                        </button>
                      ) : (
                        <button
                          onClick={() => reverseMovement(m)}
                          data-testid={`movement-reverse-${m.id}`}
                          className="inline-flex items-center gap-1 text-white/60 hover:text-[#00A651] font-condensed uppercase tracking-widest text-[10px]"
                        >
                          <Undo2 className="w-3 h-3" />
                          Rückbuchen
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default StockMovementsPanel;
